/** Angular Imports */
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

/** rxjs Imports */
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

/** Custom Services */
import { ClientsService } from '../clients.service';

/**
 * Client Identities resolver.
 */
@Injectable()
export class ClientIdentitiesResolver {
  private clientsService = inject(ClientsService);

  /**
   * Returns the Client Identities data with their associated documents.
   * Properly chains the forkJoin to ensure documents are populated before resolving.
   * Falls back to empty data on error so the page still loads.
   * @returns {Observable<any>}
   */
  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    const clientId = route.parent.paramMap.get('clientId');
    return this.clientsService.getClientIdentifiers(clientId).pipe(
      switchMap((identities: any) => {
        if (!identities || identities.length === 0) {
          return of([]);
        }
        const docObservables: Observable<any>[] = identities.map((identity: any) =>
          this.clientsService.getClientIdentificationDocuments(identity.id).pipe(
            catchError(() => of([]))
          )
        );
        return forkJoin(docObservables).pipe(
          map((documents: any[]) => {
            documents.forEach((document: any, index: number) => {
              identities[index].documents = document;
            });
            return identities;
          })
        );
      }),
      catchError(() => of([]))
    );
  }
}